// @ts-nocheck -- verbatim TAPP payroll UI compatibility boundary
type ProfileSource = {
  payrollEmployeeCode?: string;
  payrollEmail?: string;
  payrollPdfPasswordConfigured?: boolean;
  payrollCalculationEnabled?: boolean;
  payrollStartDate?: string;
  payrollEndDate?: string;
  payrollProfileNotes?: string;
  payrollAnnualHolidayEntitlement?: number;
};

type ProfileDraft = {
  payrollEmployeeCode: string;
  payrollEmail: string;
  payrollPdfPassword: string;
  payrollCalculationEnabled: boolean;
  payrollStartDate: string;
  payrollEndDate: string;
  payrollProfileNotes: string;
  payrollAnnualHolidayEntitlement: string;
};

type PeriodSource = {
  validFromMonth: string;
  validToMonth: string;
  contractType: string;
  contractualRate?: string | null;
  workloadPercent: number;
  hoursPerDay: number;
  annualHolidayEntitlement: number;
  calculationEnabled: boolean;
};

type PeriodDraft = {
  validFromMonth: string;
  validToMonth: string;
  contractType: string;
  contractualRate?: string | null;
  workloadPercent: string;
  hoursPerDay: string;
  annualHolidayEntitlement: string;
  calculationEnabled: boolean;
};

const dateValue = (value?: string) => /^\d{4}-\d{2}$/.test(String(value ?? "")) ? `${value}-01` : value ?? "";

const displayValue = (value: string) => value || "not set";

const periodRange = (from: string, to: string) => `${from || "unspecified"} to ${to || "Present"}`;

export const profileAutomaticReason = (source: ProfileSource, draft: ProfileDraft) => {
  const changes: string[] = [];
  if (draft.payrollCalculationEnabled !== (source.payrollCalculationEnabled ?? true)) {
    changes.push(`employee-wide calculation ${draft.payrollCalculationEnabled ? "enabled" : "disabled"}`);
  }
  if (draft.payrollEmployeeCode !== (source.payrollEmployeeCode ?? "")) {
    changes.push(`employee code changed to ${displayValue(draft.payrollEmployeeCode)}`);
  }
  if (draft.payrollEmail !== (source.payrollEmail ?? "")) {
    changes.push(`payroll email changed to ${displayValue(draft.payrollEmail)}`);
  }
  if (draft.payrollPdfPassword) {
    changes.push("PDF password replaced");
  }
  if (draft.payrollStartDate !== dateValue(source.payrollStartDate)) {
    changes.push(`employment start changed to ${displayValue(draft.payrollStartDate)}`);
  }
  if (draft.payrollEndDate !== dateValue(source.payrollEndDate)) {
    changes.push(`employment end changed to ${displayValue(draft.payrollEndDate)}`);
  }
  if (draft.payrollProfileNotes !== (source.payrollProfileNotes ?? "")) {
    changes.push("profile notes updated");
  }
  if (Number.parseFloat(draft.payrollAnnualHolidayEntitlement || "0") !== Number(source.payrollAnnualHolidayEntitlement ?? 20)) changes.push(`holiday entitlement changed to ${draft.payrollAnnualHolidayEntitlement} days/year`);
  return `Updated employee payroll profile: ${changes.join("; ") || "profile details changed"}.`;
};

export const periodAutomaticReason = (source: PeriodSource, draft: PeriodDraft) => {
  const changes: string[] = [];
  if (draft.calculationEnabled !== source.calculationEnabled) {
    changes.push(`calculation ${draft.calculationEnabled ? "included" : "excluded"}`);
  }
  if (draft.validFromMonth !== source.validFromMonth || draft.validToMonth !== source.validToMonth) {
    changes.push(`effective dates changed to ${periodRange(draft.validFromMonth, draft.validToMonth)}`);
  }
  if ((draft.contractualRate?.trim().replace(",", ".") || null) !== (source.contractualRate ?? null)) changes.push("contractual rate updated");
  if (draft.contractType !== source.contractType) changes.push(`contract type changed to ${draft.contractType.replaceAll("_", " ")}`);
  if (Number.parseFloat(draft.workloadPercent || "0") !== Number(source.workloadPercent)) changes.push(`workload changed to ${draft.workloadPercent}%`);
  if (Number.parseFloat(draft.hoursPerDay || "0") !== Number(source.hoursPerDay)) changes.push(`hours per day changed to ${draft.hoursPerDay}`);
  if (Number.parseFloat(draft.annualHolidayEntitlement || "0") !== Number(source.annualHolidayEntitlement)) {
    changes.push(`holiday entitlement changed to ${draft.annualHolidayEntitlement} days`);
  }
  return `Updated contract period ${periodRange(source.validFromMonth, source.validToMonth)}: ${changes.join("; ") || "period details changed"}.`;
};

export const newPeriodAutomaticReason = (draft: PeriodDraft) =>
  `Added contract period ${periodRange(draft.validFromMonth, draft.validToMonth)} with calculation ${draft.calculationEnabled ? "included" : "excluded"}.`;

export const resolveAuditReason = (customReason: string, automaticReason: string) => {
  const custom = customReason.trim();
  if (!custom) return automaticReason;
  if (custom.length >= 15) return custom;
  return `${automaticReason} Note: ${custom}`;
};
